"use client";

import { useEffect, useRef } from "react";
import { getLocalUserId } from "@/lib/identity/client";
import { getUnreadCount, setUnreadCount, incrementUnreadCount } from "./storage";

const POLL_INTERVAL_MS = 20000; // 20 seconds (FR-2.14)

/**
 * Hook for polling notifications and managing unread count
 * Polls GET /api/notifications every 20 seconds when page is visible
 * Updates localStorage unread count when new notifications are detected
 */
export function useNotifications() {
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const lastNotificationIdsRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    const pollNotifications = async () => {
      // Only poll when page is visible (Page Visibility API)
      if (document.hidden) {
        return;
      }

      const localUserId = getLocalUserId();
      if (!localUserId) {
        return;
      }

      try {
        const response = await fetch(
          `/api/notifications?local_user_id=${encodeURIComponent(localUserId)}`
        );

        if (!response.ok) {
          console.error("Failed to fetch notifications:", response.status);
          return;
        }

        const data = await response.json();
        const notifications = data.notifications || [];

        // Track which notifications we've seen before
        const currentIds: Set<string> = new Set(notifications.map((n: { id: string }) => n.id));
        const lastIds = lastNotificationIdsRef.current;

        // Find new notifications (ones we haven't seen before)
        const newNotifications = notifications.filter(
          (n: { id: string }) => !lastIds.has(n.id)
        );

        // Update the last seen IDs
        lastNotificationIdsRef.current = currentIds;

        // Sync the unread count with the actual unread notifications from the API
        // This is the source of truth - count unread notifications
        const unreadNotifications = notifications.filter(
          (n: { read: boolean }) => !n.read
        );
        const actualUnreadCount = unreadNotifications.length;
        const storedCount = getUnreadCount();

        // Update if count changed (either new notifications or read status changed)
        if (actualUnreadCount !== storedCount) {
          setUnreadCount(actualUnreadCount);
          window.dispatchEvent(new Event("vocly:unreadCountChanged"));
        }

        // Dispatch event after polling to sync notifications page with badge polling
        // This ensures notifications page stays in sync when notifications are filtered out (e.g., deleted posts)
        window.dispatchEvent(new Event("vocly:notificationsPolled"));
      } catch (error) {
        console.error("Error polling notifications:", error);
      }
    };

    // Initial poll
    pollNotifications();

    // Set up polling interval
    intervalRef.current = setInterval(pollNotifications, POLL_INTERVAL_MS);

    // Handle visibility changes - pause polling when tab is hidden
    const handleVisibilityChange = () => {
      if (document.hidden) {
        // Page is hidden - clear interval
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
          intervalRef.current = null;
        }
      } else {
        // Page is visible - resume polling
        if (!intervalRef.current) {
          pollNotifications(); // Poll immediately
          intervalRef.current = setInterval(pollNotifications, POLL_INTERVAL_MS);
        }
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, []);
}
